import i18next, { t } from "i18next";
import moment from "moment";
import { useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  GeneralButton,
  GeneralRadio,
  InfiniteScroll,
  Modal,
  SearchComponent,
} from "../../../../components";
import { EndPoints } from "../../../../Constants/endpoints";
import { useGetData } from "../../../../hooks";
import {
  PrescriptionDrug,
  PrescriptionType,
} from "../../../../interface/GeneralResponse";
import { setSelectedPrescription } from "../../../../redux/reducers/selectedPrescriptionSlice";
import { MedicationLoader } from "../../../../Share";

type AddPrescriptionModalProps = {
  isOpen: boolean;
  onClose: () => void;
  patientData: any;
};

const AddPrescriptionModal: React.FC<AddPrescriptionModalProps> = ({
  isOpen,
  onClose,
  patientData,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [selectedOption, setSelectedOption] = useState<string>("");
  const [selectedTemplate, setSelectedPrescriptionState] =
    useState<PrescriptionType | null>(null);
  const [templateSearchQuery, setTemplateSearchQuery] = useState<string>("");
  const [templatesData, setTemplatesData] = useState<PrescriptionType[]>();

  const handleOptionSelect = (option: string) => setSelectedOption(option);

  const handleTemplateSearchSubmit = (data: string) => {
    setTemplateSearchQuery(data?.trim().toLowerCase() || "");
  };

  const handleChange = (template: PrescriptionType) => {
    if (template) {
      setSelectedPrescriptionState(template);
    }
  };

  moment.locale(i18next.language);
  const handleAddPrescriptionNoTemplate = () => {
    if (patientData) {
      // dispatch(setPatientDetails(patientData));
      navigate(`/add_prescription_to_patient/${patientData?.Id}`);
      onClose();
    }
  };

  const handleAddPrescriptionWithTemplate = () => {
    if (patientData && selectedTemplate) {
      // dispatch(setPatientDetails(patientData));
      dispatch(setSelectedPrescription(selectedTemplate));
      navigate(`/add_prescription_by_template/${patientData?.Id}`);
      onClose();
    }
  };

  const { data: prescriptions } = useGetData<PrescriptionDrug[]>(
    EndPoints.GetPrescriptions,
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      header={t("add_prescription")}
      ariaLabel="Add Prescription Modal"
    >
      {!selectedOption && (
        <div className="flex flex-col pb-6">
          <div
            className="hover:bg-primary-bg flex cursor-pointer items-center rounded-lg p-4 text-secondaryDark hover:font-bold hover:text-primary"
            onClick={handleAddPrescriptionNoTemplate}
          >
            {t("new_prescription")}
          </div>

          {prescriptions?.Data?.length !== 0 && (
            <div
              className="hover:bg-primary-bg flex cursor-pointer items-center rounded-lg p-4 text-secondaryDark hover:font-bold hover:text-primary"
              onClick={() => handleOptionSelect("from_template")}
            >
              {t("from_template")}
            </div>
          )}
        </div>
      )}

      {selectedOption === "from_template" && (
        <div className="flex flex-col gap-4">
          <h3 className="text-xl font-semibold">{t("select_a_template")}</h3>

          <SearchComponent
            searchOn="typing"
            value={templateSearchQuery}
            placeholder={t("search_templates")}
            onSearch={handleTemplateSearchSubmit}
          />

          <div className="flex max-h-60 min-h-60 flex-col gap-5 overflow-y-auto">
            {templatesData?.map((template) => (
              <GeneralRadio
                key={template.Id}
                name="template"
                label={
                  <div className="flex flex-col gap-1">
                    <span>{template?.Case}</span>
                    <p className="text-body-text text-sm">
                      {t("created")}{" "}
                      {moment
                        .unix(parseInt(template?.CreatedAt))
                        .format("DD-MMM-YYYY")}
                    </p>
                  </div>
                }
                value={template.Id.toString()}
                checked={selectedTemplate?.Id === template.Id}
                onClick={() => handleChange(template)}
              />
            ))}
            <InfiniteScroll
              Count={4}
              skeletonCount={2}
              extraDataKey="Prescriptions"
              endpoint={EndPoints.GetPrescriptions}
              getAllParams={{
                Name: templateSearchQuery,
                template: true,
              }}
              getData={(data) => setTemplatesData(data)}
              renderSkeleton={
                <div className="ps-8">
                  <MedicationLoader />
                </div>
              }
            />
          </div>

          <div className="flex justify-end gap-3">
            <GeneralButton
              type="button"
              title={t("previous")}
              color="lite"
              onClick={() => setSelectedOption("")}
            />
            <GeneralButton 
              title={t("next")}
              color="blue"
              disabled={!selectedTemplate}
              onClick={handleAddPrescriptionWithTemplate}
            />
          </div>
        </div>
      )}
    </Modal>
  );
};

export default AddPrescriptionModal;
